import { Injectable } from '@angular/core';
import { Ability, AbilityBuilder } from '@casl/ability';
import { Observable, ReplaySubject } from 'rxjs';

import { EnvironmentConfigurationService } from '../env.config.service';
import { AbilityActions, AbilitySites, Role } from './ability-definitions';

@Injectable({
  providedIn: 'root'
})
export class AbilityInitializer {
  private missingRoles$ = new ReplaySubject<boolean>(1);

  constructor(private envConfigService: EnvironmentConfigurationService, private ability: Ability) {}

  initializeAbilities(roles: string[]): void {
    // Lets clear the abilities if there would be any leftovers
    this.removeAllAbilities();
    // Convert our roles into ability-rules
    const abilityRulesFromRoles = this.defineAbilitiesFor(roles);
    // Now set the rules
    this.ability.update(abilityRulesFromRoles);
  }

  removeAllAbilities(): void {
    this.ability.update([]);
  }

  getMissingRoles(): Observable<boolean> {
    // Only publish the replaysubject as observable so that consumers are not able to publish events on their own
    return this.missingRoles$.asObservable();
  }

  private defineAbilitiesFor(roles: string[]) {
    const { can, rules } = new AbilityBuilder<Ability>();

    if (this.envConfigService.environment.allowRolesFeature) {
      if (roles.includes(Role.submitter)) {
        this.addSubmitterPermissions(can);
      }

      if (roles.includes(Role.admin)) {
        this.addAdminPermissions(can);
      }

      if (roles.includes(Role.editor)) {
        this.addEditorPermissions(can);
      }

      if (roles.includes(Role.readOnly)) {
        this.addReadOnlyPermissions(can);
      }
    } else {
      // The roleFeature is not enabled, so just add all available permissions.
      this.addSubmitterPermissions(can);
      this.addAdminPermissions(can);
      this.addEditorPermissions(can);
      this.addReadOnlyPermissions(can);
    }

    // Check if the user does not have any of the needed roles for SCIP
    if (!rules.length) {
      // Lets tell the listeners that the current user has no roles, let the listener decide what to do with that info
      this.missingRoles$.next(true);
    }

    return rules;
  }

  private addReadOnlyPermissions(can: any) {
    // Dossier object
    can(AbilityActions.read, AbilitySites.submission);

    // Product
    can(AbilityActions.read, AbilitySites.product);

    // Preferences
    can(AbilityActions.read, AbilitySites.preferences);
  }

  private addEditorPermissions(can: any) {
    // Product
    can(AbilityActions.read, AbilitySites.product);
    can(AbilityActions.update, AbilitySites.product);
    can(AbilityActions.read, AbilitySites.submission);
    // Preferences
    can(AbilityActions.read, AbilitySites.preferences);
  }

  private addAdminPermissions(can: any) {
    can(AbilityActions.read, AbilitySites.product);
    can(AbilityActions.read, AbilitySites.submission);
    can(AbilityActions.read, AbilitySites.preferences);
    can(AbilityActions.update, AbilitySites.preferences);
    can(AbilityActions.create, AbilitySites.preferences);
    // for ECHA profile setup, we only distinguish between readonly access and can edit
    can(AbilityActions.update, AbilitySites.echaprofile);
    can(AbilityActions.read, AbilitySites.envoverview);
  }

  private addSubmitterPermissions(can: any) {
    // Dossier object
    can(AbilityActions.read, AbilitySites.submission);
    can(AbilityActions.update, AbilitySites.submission);
    can(AbilityActions.create, AbilitySites.submission);

    // Product
    can(AbilityActions.read, AbilitySites.product);

    // Preferences
    can(AbilityActions.read, AbilitySites.preferences);
  }
}
